import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { Loader2 } from 'lucide-react';
import { AccessDenied } from './AccessDenied';

interface ProtectedRouteProps {
  allowedRoles?: string[];
  requiredPermission?: string;
  requiredPermissions?: string[];
}

export function ProtectedRoute({
  allowedRoles,
  requiredPermission,
  requiredPermissions
}: ProtectedRouteProps) {
  const { session, user, loading, initialLoading, authState, hasRole, hasPermission, hasAnyPermission } = useAuth();
  const location = useLocation();

  if (authState === 'AUTH_INITIALIZING' || initialLoading || loading) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (authState === 'AUTH_UNAUTHENTICATED' || !session || !user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // Force password change check
  if (user.mustChangePassword && location.pathname !== '/force-password-change') {
    return <Navigate to="/force-password-change" replace />;
  }

  // User status lifecycle check
  if (user.status === 'DISABLED') {
    return <Navigate to="/login?error=disabled" replace />;
  }

  if (user.status === 'SUSPENDED') {
    return <Navigate to="/login?error=suspended" replace />;
  }

  if (user.status === 'PENDING' || user.status === 'INVITED') {
    if (location.pathname !== '/access-pending') {
      return <Navigate to="/access-pending" replace />;
    }
  }

  // Role validation
  if (allowedRoles && allowedRoles.length > 0) {
    if (!hasRole(allowedRoles)) {
      return <AccessDenied message="You do not have permission to access this page." />;
    }
  }

  // Single required permission check
  if (requiredPermission) {
    if (!hasPermission(requiredPermission)) {
      return <AccessDenied message="You do not have permission to access this page." />;
    }
  }

  // Multiple required permissions check
  if (requiredPermissions && requiredPermissions.length > 0) {
    if (!hasAnyPermission(requiredPermissions)) {
      return <AccessDenied message="You do not have permission to access this page." />;
    }
  }

  return <Outlet />;
}
